import {
  NumberDispositionEnum,
  useDeleteWorkspaceUserMutation,
  useRemoveWorkspaceUserWithDispositionMutation,
} from '@/types/generated/graphql';
import { notification } from 'antd';
import { useState } from 'react';
import {
  cacheDeleteMember,
  decreaseTotalMemberCountInCache,
  invalidateMyNumbersChannelsCache,
} from '../member-handlers';
import { useChannelReward } from '../../my-numbers/features/claim-numbers/hooks/use-channel-reward';
import { useClaimedFreeNumbers } from '../../my-numbers/features/claim-numbers/hooks/use-claimed-free-numbers';
import { RemoveDispositionInput, TDeleteData } from '../members.types';
import { cache } from '@/container/apollo-client/cache';

export const useDeleteMember = () => {
  const [deleteData, setDeleteData] = useState<TDeleteData | null>();

  // When the workspace has no free-number rewards left to claim, removing a member who
  // holds a claimed free number needs an explicit disposition (delete vs purchase).
  // While rewards remain, removal is a straightforward confirmation.
  const { requiresDisposition, refetch } = useChannelReward();

  // The claimed-free-numbers list is only relevant inside the disposition flow.
  const { claimedFreeNumbers } = useClaimedFreeNumbers({
    skip: !requiresDisposition,
  });

  const requiresFreeNumberDisposition = requiresDisposition;

  const requestDeleteMember = async (id: number, name: string) => {
    await refetch();
    setDeleteData({ id, name });
  };

  const [deleteMember, { loading: deleteMemberLoading }] = useDeleteWorkspaceUserMutation({
    onCompleted: (res) => {
      notification.success({ message: res?.deleteWorkspaceUser?.message });
      cacheDeleteMember(res?.deleteWorkspaceUser?.data?.id as number);
      invalidateMyNumbersChannelsCache();
      decreaseTotalMemberCountInCache();
      handleCancel();
    },
    onError: (error) => {
      notification.error({ message: error.message });
    },
  });

  const handleDeleteMember = () => {
    if (deleteData?.id) {
      return deleteMember({
        variables: {
          deleteWorkspaceUserId: deleteData?.id,
        },
      });
    }
  };

  const handleCancel = () => {
    setDeleteData(null);
  };

  const [removeWithDisposition] = useRemoveWorkspaceUserWithDispositionMutation();

  // Atomically release (or purchase) the member's claimed free number and remove them.
  // gatewayAccountId is only forwarded for the purchase disposition.
  const handleRemoveWithDisposition = async (input: RemoveDispositionInput): Promise<{ message: string }> => {
    const userWorkspaceId = deleteData?.id;
    if (!userWorkspaceId) throw new Error('No member selected for removal.');

    const result = await removeWithDisposition({
      variables: {
        input: {
          userWorkspaceId,
          channelId: Number(input.channelId),
          disposition: input.disposition as NumberDispositionEnum,
          ...(input.disposition === 'purchase' && input.gatewayAccountId
            ? { gatewayAccountId: input.gatewayAccountId }
            : {}),
        },
      },
    });

    cacheDeleteMember(userWorkspaceId);
    decreaseTotalMemberCountInCache();
    try {
      cache.modify({
        fields: {
          channelsV2: () => {},
          assignChannelsV2: () => {},
          workspaceClaimedFreeNumbers: () => {},
          getAssignedChannelsCount: () => {},
          listSubscription: () => {},
          checkConfigurationAndRewardOfChannel: () => {},
        },
      });
    } catch (error) {
      console.error('Error invalidating cache during number removal ', error);
    }

    return { message: result.data?.removeWorkspaceUserWithDisposition?.message ?? '' };
  };

  return {
    handleDeleteMember,
    deleteMemberLoading,
    requestDeleteMember,
    deleteData,
    handleCancel,
    requiresFreeNumberDisposition,
    claimedFreeNumbers,
    handleRemoveWithDisposition,
  };
};
